import React, { useEffect, useState } from 'react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const card = "p-4 bg-gray-800/80 border border-gray-700/60 rounded-xl hover:border-orange-500/50 transition-colors";

const PlayStoreLaunchPage = () => {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/play-store/manifest`)
      .then((r) => r.json())
      .then(setManifest)
      .catch((e) => setError(String(e)));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 text-white p-8" data-testid="play-store-error">
        <p>Failed to load manifest: {error}</p>
      </div>
    );
  }
  if (!manifest) {
    return (
      <div className="min-h-screen bg-gray-950 text-white p-8 flex items-center justify-center" data-testid="play-store-loading">
        Loading launch package…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-white p-6 sm:p-10" data-testid="play-store-page">
      <div className="max-w-4xl mx-auto">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-widest text-orange-400 mb-2">Owner only</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">Play Store launch package</h1>
          <p className="text-gray-400 max-w-2xl">
            Every asset, every form answer, and the production-ready AAB — all served from your own domain so there are no
            cross-site warnings when you drag-and-drop into the Play Console.
          </p>
          <div className="mt-4 inline-flex items-center gap-3 text-sm bg-orange-500/10 border border-orange-500/30 px-4 py-2 rounded-lg">
            <span className="font-mono">{manifest.package}</span>
            <span className="text-gray-500">·</span>
            <span>v{manifest.version_name} · code {manifest.version_code}</span>
          </div>
        </header>

        <section className="mb-10">
          <h2 className="text-lg sm:text-lg font-semibold mb-4">Step 1 — Read the guide</h2>
          <a
            href={`${API}${manifest.launch_guide}`}
            target="_blank"
            rel="noreferrer"
            className={`${card} flex items-center justify-between group`}
            data-testid="play-store-guide-link"
          >
            <div>
              <div className="font-semibold mb-1">Launch guide (Markdown)</div>
              <div className="text-sm text-gray-400">
                Full step-by-step with every copy-paste field, screenshot slot, and content-rating answer.
              </div>
            </div>
            <span className="text-orange-400 group-hover:translate-x-1 transition-transform">→</span>
          </a>
        </section>

        <section className="mb-10">
          <h2 className="text-lg sm:text-lg font-semibold mb-4">Step 2 — Download the AAB and APK</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <a
              href={`${API}${manifest.aab_download}`}
              className={`${card} block`}
              data-testid="play-store-aab-download"
            >
              <div className="font-semibold mb-1">android.aab</div>
              <div className="text-sm text-gray-400">48 MB · upload to Play Console</div>
            </a>
            <a
              href={`${API}${manifest.apk_download}`}
              className={`${card} block`}
              data-testid="play-store-apk-download"
            >
              <div className="font-semibold mb-1">android.apk</div>
              <div className="text-sm text-gray-400">93 MB · install on your phone right now</div>
            </a>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-lg sm:text-lg font-semibold mb-4">Step 3 — Graphic assets</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <a
              href={`${API}${manifest.app_icon_512}`}
              target="_blank"
              rel="noreferrer"
              className={`${card} block`}
              data-testid="play-store-icon-link"
            >
              <div className="font-semibold mb-1">App icon · 512×512</div>
              <div className="text-sm text-gray-400">app_icon_512.png</div>
            </a>
            <a
              href={`${API}${manifest.feature_graphic_1024x500}`}
              target="_blank"
              rel="noreferrer"
              className={`${card} block`}
              data-testid="play-store-feature-graphic-link"
            >
              <div className="font-semibold mb-1">Feature graphic · 1024×500</div>
              <div className="text-sm text-gray-400">feature_graphic_1024x500.png</div>
            </a>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-lg sm:text-lg font-semibold mb-4">Step 4 — Phone screenshots (1080×1920)</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {manifest.screenshots_phone_1080x1920.map((url) => {
              const name = url.split('/').pop();
              return (
                <a
                  key={name}
                  href={`${API}${url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-lg overflow-hidden border border-gray-700 hover:border-orange-500/60 transition-colors"
                  data-testid={`play-store-screenshot-${name}`}
                >
                  <img
                    src={`${API}${url}`}
                    alt={name}
                    className="w-full aspect-[9/16] object-cover bg-gray-900"
                    loading="lazy"
                  />
                  <div className="text-xs text-gray-400 px-2 py-1 truncate">{name}</div>
                </a>
              );
            })}
          </div>
        </section>

        <section className="mb-10 text-sm text-gray-500">
          <p>
            Tip: Right-click any link → "Save link as…" if your browser tries to display the asset instead of downloading it.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PlayStoreLaunchPage;
